import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import DateInput, { DateChangeEvent } from 'carbon-react/lib/components/date';
import { Option as SelectOption, Select } from 'carbon-react/lib/components/select';
import Textbox from 'carbon-react/lib/components/textbox';
import Box from 'carbon-react/lib/components/box';

import * as styles from './recurrence-settings.scss';
import { RecurrenceEndType } from './types';

interface EndSettingsProps {
  recurrenceEndType: RecurrenceEndType;
  occurancesValue: string;
  recurrenceEndDate: Date;
  recurrenceEndDateIsBeforeStartDate: boolean;
  onEndTypeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onOccurancesValueChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onEndDateChange: (e: DateChangeEvent) => void;
}

export const EndSettings = ({
  recurrenceEndType,
  occurancesValue,
  recurrenceEndDate,
  recurrenceEndDateIsBeforeStartDate,
  onEndTypeChange,
  onOccurancesValueChange,
  onEndDateChange
}: EndSettingsProps) => {
  const intl = useIntl();

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <Select
        required
        label={intl.formatMessage({ id: 'common.ends', defaultMessage: 'Ends' })}
        value={recurrenceEndType}
        onChange={onEndTypeChange}
        mr="8px"
        mb="0"
        style={{ width: 140 }}
        size="medium"
      >
        <SelectOption
          value={RecurrenceEndType.ON_THIS_DAY}
          text={intl.formatMessage({ id: 'reporting.on_date', defaultMessage: 'On date' })}
        />
        <SelectOption
          value={RecurrenceEndType.AFTER_N_OCCURANCES}
          text={intl.formatMessage({ id: 'common.after_n_occurrences_capital', defaultMessage: 'After' })}
        />
        <SelectOption
          value={RecurrenceEndType.NEVER}
          text={intl.formatMessage({ id: 'common.never', defaultMessage: 'Never' })}
        />
      </Select>
      {recurrenceEndType === RecurrenceEndType.AFTER_N_OCCURANCES && (
        <Box mt="29px" display="flex" alignItems="center">
          <Textbox
            value={occurancesValue}
            onChange={e => {
              if (!/^-?[0-9]*$/.test(e.target.value)) {return;}
              onOccurancesValueChange(e);
            }}
            mr="8px"
            mb="0"
            size="medium"
            style={{ width: '48px' }}
          />
          <span className={styles.labelFont} style={{ fontWeight: 400 }}>
            <FormattedMessage
              id="common.occurrence_s_no_space"
              defaultMessage="Occurrence(s)"
              values={{ count: occurancesValue }}
            />
          </span>
        </Box>
      )}
      {recurrenceEndType === RecurrenceEndType.ON_THIS_DAY && (
        <DateInput
          mb="0"
          mt="29px"
          width="180px"
          value={intl.formatDate(recurrenceEndDate)}
          onChange={onEndDateChange}
          error={
            recurrenceEndDateIsBeforeStartDate
              ? intl.formatMessage({
                  id: 'reporting.reset_end_date_future_date',
                  defaultMessage: 'Reset the end date to a future date'
                })
              : ''
          }
        />
      )}
    </div>
  );
};
